import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { X, AlertCircle, Loader, Instagram } from 'lucide-react';
import { useStore } from '../store';
import { motion, AnimatePresence } from 'framer-motion';
import { isSupabaseStorageUrl } from '../utils/video/player';
import { fetchInstagramThumbnail } from '../utils/fetchInstagramThumbnail';
import { scanInstagramAudio } from '../utils/scanInstagramAudio';
import { Upload, Loader as LoaderIcon, X as XIcon, Image, FolderDown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useArtistClient } from '../contexts/ArtistClientContext';
import { VideoUploader } from './VideoUploader';
import { normalizeSpotifyUri, uploadReleaseCover, isReleaseInCurrentWeek } from '../lib/releaseAssets';
import JSZip from 'jszip';

interface AdCreativeSubmissionModalProps {
  onClose: () => void;
  artistId?: string;
  isOpen?: boolean;
  isArtistView?: boolean;
}

export function AdCreativeSubmissionModal({ onClose, artistId, isOpen = true, isArtistView = false }: AdCreativeSubmissionModalProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const [selectedArtist, setSelectedArtist] = useState(artistId || '');
  const [selectedRelease, setSelectedRelease] = useState<string>('');

  // Sync internal selectedArtist with the artistId prop when it changes
  useEffect(() => {
    if (artistId) {
      setSelectedArtist(artistId);
    }
  }, [artistId]);
  const [releases, setReleases] = useState<any[]>([]);
  const [socialMediaUrls, setSocialMediaUrls] = useState('');
  const [dropboxUrls] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [uploadedFileNames, setUploadedFileNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [uploadStats, setUploadStats] = useState<{
    remainingTime: number;
    uploadSpeed: number;
    fileSize: string;
  } | null>(null);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [hoveredUrl, setHoveredUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [folderUrl, setFolderUrl] = useState('');

  // Per-release cover + Spotify URI quick-upload (saves instantly to the
  // release row; the section hides itself once both assets exist).
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [uriDraft, setUriDraft] = useState('');
  const [uriError, setUriError] = useState<string | null>(null);
  const [savingUri, setSavingUri] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverJustUploaded, setCoverJustUploaded] = useState<string | null>(null);
  
  // Dropbox folder processing state
  const [isProcessingFolder, setIsProcessingFolder] = useState(false);
  const [folderProcessLogs, setFolderProcessLogs] = useState<Array<{message: string; type: 'info' | 'success' | 'error' | 'progress'; timestamp: string}>>([]);
  
  // Multi-file upload progress state
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [totalFilesToUpload, setTotalFilesToUpload] = useState(0);
  const [currentFileName, setCurrentFileName] = useState('');
  const [uploadLogs, setUploadLogs] = useState<Array<{message: string; type: 'info' | 'success' | 'error' | 'progress'}>>([]);

  const { artists, adCreatives, submissions, fetchAdCreatives } = useStore();
  const { client: artistClient } = useArtistClient();
  const db = artistClient || supabase;

  // Existing creatives for the selected release (used for duplicate detection).
  // Duplicate detection is release-scoped: the same URL/code can be submitted to
  // a different release, but not twice within the same release.
  // Maps normalized URL → { status, meta_ad_status } so the IG posts grid can
  // show whether a post is already added and its current ad status.
  const [existingCreativesForRelease, setExistingCreativesForRelease] = useState<Map<string, { status: string; meta_ad_status: string | null }>>(new Map());

  // Recent IG posts from the artist's connected Instagram account
  const [igRecentPosts, setIgRecentPosts] = useState<Array<{
    id: string;
    permalink: string;
    media_type: string;
    thumbnail: string;
    caption: string;
    timestamp: string;
  }>>([]);
  const [igPostsLoading, setIgPostsLoading] = useState(false);
  const [igUsername, setIgUsername] = useState<string | null>(null);
  const [showIgPosts, setShowIgPosts] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Audio scan results for IG posts: permalink → { audio_type, licensed, loading }
  const [igAudioScan, setIgAudioScan] = useState<Record<string, { audio_type: string | null; licensed: boolean; loading: boolean; error?: string }>>({});

  // Feedbacktool Supabase URL for edge functions
  const FEEDBACKTOOL_SUPABASE_URL = 'https://wrlgoxbzlngdtomjhvnz.supabase.co';

  // Fetch recent IG posts when an artist is selected
  const fetchIgRecentPosts = useCallback(async (artistId: string) => {
    if (!artistId) {
      setIgRecentPosts([]);
      setIgUsername(null);
      return;
    }
    setIgPostsLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || '';
      const resp = await fetch(`${FEEDBACKTOOL_SUPABASE_URL}/functions/v1/list-ig-recent-posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ artist_id: artistId }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setIgRecentPosts([]);
        setIgUsername(null);
      } else {
        setIgRecentPosts(data.posts || []);
        setIgUsername(data.ig_username || null);
      }
    } catch (err) {
      setIgRecentPosts([]);
    } finally {
      setIgPostsLoading(false);
    }
  }, [FEEDBACKTOOL_SUPABASE_URL]);

  // Check if current user is admin (admin/management team)
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      const team = user?.user_metadata?.team;
      setIsAdmin(team === 'admin' || team === 'management');
    });
  }, []);

  // Fetch when artist changes — admin only
  useEffect(() => {
    if (selectedArtist && isAdmin) {
      fetchIgRecentPosts(selectedArtist);
    } else {
      setIgRecentPosts([]);
      setIgUsername(null);
    }
  }, [selectedArtist, isAdmin, fetchIgRecentPosts]);

  // Scan audio type for IG posts in real-time (check_only mode — no DB writes)
  const scanIgPostAudio = useCallback(async (permalink: string) => {
    setIgAudioScan(prev => ({ ...prev, [permalink]: { audio_type: null, licensed: false, loading: true } }));
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const res = await fetch(`${supabaseUrl}/functions/v1/scan-instagram-audio`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${anonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ check_only: true, instagram_url: permalink }),
      });
      const data = await res.json();
      setIgAudioScan(prev => ({
        ...prev,
        [permalink]: {
          audio_type: data.audio_type || null,
          licensed: !!data.licensed,
          loading: false,
          error: data.error,
        },
      }));
    } catch (err) {
      setIgAudioScan(prev => ({
        ...prev,
        [permalink]: { audio_type: null, licensed: false, loading: false, error: (err as Error).message },
      }));
    }
  }, []);

  // Trigger audio scan for all loaded IG posts (staggered to avoid rate limits)
  useEffect(() => {
    if (igRecentPosts.length === 0) {
      setIgAudioScan({});
      return;
    }
    // Only scan posts not already scanned
    const toScan = igRecentPosts.filter(p => !igAudioScan[p.permalink]);
    if (toScan.length === 0) return;
    // Stagger: scan 1 at a time with 300ms delay
    toScan.forEach((post, i) => {
      setTimeout(() => scanIgPostAudio(post.permalink), i * 300);
    });
  }, [igRecentPosts, scanIgPostAudio]);

  // Check if URL is a Dropbox folder URL
  const isDropboxFolderUrl = (url: string): boolean => {
    return url.includes('dropbox.com/scl/fo/') || url.includes('dropbox.com/sh/');
  };

  const addFolderLog = (message: string, type: 'info' | 'success' | 'error' | 'progress' = 'info') => {
    setFolderProcessLogs(prev => [...prev, { message, type, timestamp: new Date().toISOString() }]);
  };

  // Process Dropbox folder URL — uses edge function as CORS proxy, then extracts + uploads client-side
  const processDropboxFolder = async () => {
    const url = folderUrl.trim();
    if (!url) {
      setError('Please paste a Dropbox folder URL');
      return;
    }
    if (!isDropboxFolderUrl(url)) {
      setError('Please provide a valid Dropbox folder URL (contains /scl/fo/ or /sh/)');
      return;
    }
    if (!selectedArtist) {
      setError('Please select an artist first');
      return;
    }

    setIsProcessingFolder(true);
    setFolderProcessLogs([]);
    setError(null);

    try {
      // Get auth token
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token || '';

      addFolderLog('Downloading ZIP via proxy...', 'progress');

      const response = await fetch(`${FEEDBACKTOOL_SUPABASE_URL}/functions/v1/process-dropbox-folder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ dropbox_folder_url: url }),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(`Proxy returned ${response.status}: ${errText}`);
      }

      const contentLength = response.headers.get('content-length');
      const totalSize = contentLength ? parseInt(contentLength, 10) : null;
      if (totalSize) {
        addFolderLog(`File size: ${(totalSize / 1024 / 1024).toFixed(1)} MB`, 'info');
      }

      // Read ZIP data with progress
      const reader = response.body?.getReader();
      if (!reader) throw new Error('Failed to read response stream');

      const chunks: Uint8Array[] = [];
      let downloadedBytes = 0;
      const startTime = Date.now();
      let lastProgressUpdate = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        downloadedBytes += value.length;

        const now = Date.now();
        if (now - lastProgressUpdate > 500) {
          const downloadedMB = downloadedBytes / 1024 / 1024;
          const elapsedSec = (now - startTime) / 1000;
          const speedMBps = elapsedSec > 0 ? downloadedMB / elapsedSec : 0;
          if (totalSize) {
            const pct = Math.round((downloadedBytes / totalSize) * 100);
            const remainingMB = (totalSize - downloadedBytes) / 1024 / 1024;
            const etaSec = speedMBps > 0 ? remainingMB / speedMBps : 0;
            addFolderLog(`Downloading: ${downloadedMB.toFixed(1)} MB / ${(totalSize / 1024 / 1024).toFixed(1)} MB (${pct}%) — ${speedMBps.toFixed(1)} MB/s — ETA: ${Math.round(etaSec)}s`, 'progress');
          } else {
            addFolderLog(`Downloading: ${downloadedMB.toFixed(1)} MB — ${speedMBps.toFixed(1)} MB/s`, 'progress');
          }
          lastProgressUpdate = now;
        }
      }

      addFolderLog(`Downloaded ${(downloadedBytes / 1024 / 1024).toFixed(1)} MB in ${((Date.now() - startTime) / 1000).toFixed(1)}s`, 'success');

      // Combine chunks
      const totalLength = chunks.reduce((acc, c) => acc + c.length, 0);
      const zipData = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of chunks) {
        zipData.set(chunk, offset);
        offset += chunk.length;
      }
      chunks.length = 0;

      // Extract ZIP
      addFolderLog('Extracting ZIP...', 'progress');
      const zip = await JSZip.loadAsync(zipData);

      const videoExtensions = ['.mp4', '.mov', '.webm', '.avi', '.mkv', '.m4v'];
      const videoEntries = Object.entries(zip.files).filter(([filename, file]) => {
        if ((file as any).dir) return false;
        const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
        return videoExtensions.includes(ext);
      });

      if (videoEntries.length === 0) {
        addFolderLog('No video files found in the ZIP', 'error');
        return;
      }

      addFolderLog(`Found ${videoEntries.length} video files`, 'success');

      // Upload each video to Storage and add to uploadedUrls
      const newUrls: string[] = [];
      const newFileNames: Record<string, string> = {};

      for (let i = 0; i < videoEntries.length; i++) {
        const [filename, file] = videoEntries[i];
        const progress = `[${i + 1}/${videoEntries.length}]`;
        const fileName = filename.split('/').pop() || filename;
        const cleanName = fileName.replace(/\.[^/.]+$/, '');

        try {
          addFolderLog(`${progress} Extracting: ${fileName}`, 'progress');
          const videoData = await (file as any).async('uint8array');
          const fileSizeMB = (videoData.length / 1024 / 1024).toFixed(1);

          addFolderLog(`${progress} Uploading: ${fileName} (${fileSizeMB} MB)`, 'progress');

          const timestamp = Date.now();
          const randomId = Math.random().toString(36).substring(2, 15);
          const ext = fileName.substring(fileName.lastIndexOf('.'));
          const storagePath = `uploads/${timestamp}_${randomId}${ext}`;

          const { error: uploadError } = await db.storage
            .from('videos')
            .upload(storagePath, videoData, {
              contentType: `video/${ext.replace('.', '')}`,
              upsert: false,
            });

          if (uploadError) {
            addFolderLog(`${progress} Upload failed: ${uploadError.message}`, 'error');
            continue;
          }

          const { data: urlData } = db.storage.from('videos').getPublicUrl(storagePath);
          const publicUrl = urlData.publicUrl;

          newUrls.push(publicUrl);
          newFileNames[publicUrl] = cleanName;
          addFolderLog(`${progress} Uploaded: ${cleanName}`, 'success');
        } catch (err) {
          addFolderLog(`${progress} Failed: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
        }
      }

      // Add to uploaded URLs so they show in the UI with editable names
      setUploadedUrls(prev => [...prev, ...newUrls]);
      setUploadedFileNames(prev => ({ ...prev, ...newFileNames }));

      addFolderLog(`Done! ${newUrls.length} of ${videoEntries.length} videos added. Click "Submit URLs" to create ad creatives.`, 'success');
      setFolderUrl('');
    } catch (err) {
      console.error('Error processing Dropbox folder:', err);
      addFolderLog(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    } finally {
      setIsProcessingFolder(false);
    }
  };

  // Filter out archived artists
  const activeArtists = artists.filter(artist => !artist.archived);

  // Fetch releases for selected artist
  useEffect(() => {
    const fetchReleases = async () => {
      if (!selectedArtist) {
        setReleases([]);
        return;
      }

      try {
        const { data, error } = await db
          .from('releases')
          .select('id, name, release_date, created_at, status, spotify_uri, cover_url')
          .eq('artist_id', selectedArtist as any)
          .order('release_date', { ascending: false, nullsFirst: false });

        if (error) throw error;

        const releases = (data || []) as any[];
        setReleases(releases);

        // Load saved release from session storage or select default
        const savedReleaseKey = `selected_release_${selectedArtist}`;
        const savedRelease = sessionStorage.getItem(savedReleaseKey);

        if (releases && releases.length > 0) {
          if (savedRelease && releases.find((r: any) => r.id === savedRelease)) {
            // Use saved selection if it exists
            setSelectedRelease(savedRelease);
          } else if (releases.length === 1) {
            // Auto-select if only one release
            setSelectedRelease(releases[0].id);
          } else {
            // Select newest (first in list due to order by release_date desc)
            setSelectedRelease(releases[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching releases:', error);
      }
    };

    fetchReleases();
  }, [selectedArtist]);

  // Fetch all existing creatives for the selected release (for duplicate detection).
  // Stores both the raw value and the normalized/cleaned form (e.g. Instagram
  // shortcode without trailing slash or query params) so duplicate matching
  // works regardless of URL formatting.
  // Also loads by artist (regardless of release) so the IG posts grid can
  // mark already-added posts even when no release is selected.
  useEffect(() => {
    const loadExisting = async () => {
      console.log('🔍 Loading existing creatives for release:', selectedRelease || 'NONE', 'artist:', selectedArtist || 'NONE');
      try {
        const map = new Map<string, { status: string; meta_ad_status: string | null }>();
        const addBoth = (val?: string | null, status?: string, metaAdStatus?: string | null) => {
          if (!val) return;
          const trimmed = val.trim();
          if (!trimmed) return;
          const entry = { status: status || 'pending', meta_ad_status: metaAdStatus || null };
          // Don't overwrite an existing entry with a less specific one
          if (!map.has(trimmed)) map.set(trimmed, entry);
          try {
            const cleaned = cleanUrl(trimmed);
            if (cleaned && !map.has(cleaned)) map.set(cleaned, entry);
          } catch { /* ignore */ }
        };

        // Load by artist (broader — for IG posts grid marking)
        if (selectedArtist) {
          const { data: artistData, error: artistError } = await db
            .from('ad_creatives')
            .select('content, merged_instagram_reel_url, merged_tiktok_auth_code, original_instagram_url, status, meta_ad_status')
            .eq('artists_id' as any, selectedArtist as any);
          if (!artistError && artistData) {
            for (const row of (artistData as any[]) || []) {
              addBoth(row.content, row.status, row.meta_ad_status);
              addBoth(row.merged_instagram_reel_url, row.status, row.meta_ad_status);
              addBoth(row.merged_tiktok_auth_code, row.status, row.meta_ad_status);
              addBoth(row.original_instagram_url, row.status, row.meta_ad_status);
            }
            console.log(`✅ Loaded ${map.size} existing URLs for artist ${selectedArtist}`);
          }
        }

        // Load by release (more specific — for duplicate detection).
        // These entries take priority since they're release-scoped.
        if (selectedRelease) {
          const { data, error } = await db
            .from('ad_creatives')
            .select('content, merged_instagram_reel_url, merged_tiktok_auth_code, original_instagram_url, status, meta_ad_status')
            .eq('release_id' as any, selectedRelease as any);
          if (!error && data) {
            for (const row of (data as any[]) || []) {
              addBoth(row.content, row.status, row.meta_ad_status);
              addBoth(row.merged_instagram_reel_url, row.status, row.meta_ad_status);
              addBoth(row.merged_tiktok_auth_code, row.status, row.meta_ad_status);
              addBoth(row.original_instagram_url, row.status, row.meta_ad_status);
            }
            console.log(`✅ Loaded ${map.size} total URLs (incl. release ${selectedRelease})`);
          }
        }

        setExistingCreativesForRelease(map);
      } catch (err) {
        console.error('Failed to load existing creatives for duplicate check:', err);
        setExistingCreativesForRelease(new Map());
      }
    };
    loadExisting();
  }, [selectedRelease, selectedArtist]);

  // Split textarea content into individual URLs / auth codes.
  // Splits by newlines AND by inline `https://` boundaries (so two URLs pasted
  // back-to-back without a separator are treated as separate entries).
  const splitSocialMediaInput = (raw: string): string[] => {
    const out: string[] = [];
    for (const line of raw.split(/\r?\n/)) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const parts = trimmed.split(/(?=https?:\/\/)/g).map((p) => p.trim()).filter(Boolean);
      if (parts.length > 1) out.push(...parts);
      else out.push(trimmed);
    }
    return out;
  };

  // Clean/normalize URLs for duplicate detection
  const cleanUrl = (url: string): string => {
    try {
      // For Instagram URLs, remove query parameters but keep case sensitivity
      if (url.includes('instagram.com') || url.includes('instagram.com/reel') || url.includes('instagram.com/p/')) {
        const urlObj = new URL(url);
        // Normalize Instagram URLs to a standard format
        const path = urlObj.pathname;
        
        console.log(`🔧 cleanUrl processing: ${url}`);
        console.log(`   - Path: ${path}`);
        
        // Extract the post ID which is the second path segment in /p/{id}/ or /reel/{id}/ or /reels/{id}/ format
        // Or third segment in /{username}/reel/{id}/ or /{username}/reels/{id}/ or /{username}/p/{id}/ format
        let postId = '';
        const pathParts = path.split('/').filter(Boolean);
        
        console.log(`   - Path parts:`, pathParts);
        
        if (pathParts.length >= 2) {
          if (pathParts[0] === 'reel' || pathParts[0] === 'reels' || pathParts[0] === 'p') {
            // Format: /reel/{id}/ or /reels/{id}/ or /p/{id}/
            postId = pathParts[1];
          } else if (pathParts.length >= 3 && (pathParts[1] === 'reel' || pathParts[1] === 'reels' || pathParts[1] === 'p')) {
            // Format: /{username}/reel/{id}/ or /{username}/reels/{id}/ or /{username}/p/{id}/
            postId = pathParts[2];
          }
        }
        
        console.log(`   - PostId: ${postId}`);
        
        // If we found a post ID, standardize to /reel/{id} or /p/{id} format
        if (postId) {
          const type = (path.includes('/reel/') || path.includes('/reels/')) ? 'reel' : 'p';
          const result = `${urlObj.origin}/${type}/${postId}`;
          console.log(`   - Normalized result: ${result}`);
          return result;
        }
        
        // If we couldn't parse it properly, just return the cleaned URL
        const fallback = `${urlObj.origin}${path}`;
        console.log(`   - Fallback result: ${fallback}`);
        return fallback;
      }
      // For Dropbox URLs, ensure they're in the correct format
      if (url.includes('dropbox.com')) {
        const urlObj = new URL(url);
        urlObj.searchParams.set('dl', '1');
        return urlObj.toString();
      }
      // For TikTok codes, just trim whitespace and preserve case
      return url.trim();
    } catch {
      // If URL parsing fails, return original trimmed string
      return url.trim();
    }
  };

  // Extract filename from Dropbox URL
  const extractDropboxFileName = (url: string): string | null => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      // Dropbox URLs have format: /scl/fi/{id}/{filename}.ext or /s/{id}/{filename}.ext
      const pathParts = pathname.split('/');
      const lastPart = pathParts[pathParts.length - 1];
      if (lastPart && lastPart.includes('.')) {
        // Remove file extension and decode URL encoding
        const fileName = decodeURIComponent(lastPart.replace(/\.[^/.]+$/, ''));
        return fileName || null;
      }
      return null;
    } catch {
      return null;
    }
  };

  // Count Instagram and TikTok URLs
  const socialMediaLines = splitSocialMediaInput(socialMediaUrls);
  const instagramCount = socialMediaLines.filter(url => 
    url.includes('instagram.com') && (url.includes('/reel/') || url.includes('/reels/') || url.includes('/p/'))
  ).length;
  const tiktokCount = socialMediaLines.filter(url => url.trim().startsWith('#')).length;

  // Per-line duplicate status for the social media textarea
  const socialMediaLineStatus = useMemo(() => {
    console.log('🔄 Computing socialMediaLineStatus...');
    const seenInThisInput = new Set<string>();
    return socialMediaLines.map((rawLine) => {
      const trimmed = rawLine.trim();
      let normalized = trimmed;
      let kind: 'instagram' | 'tiktok' | 'unknown' = 'unknown';
      if (trimmed.startsWith('#')) {
        kind = 'tiktok';
      } else if (
        trimmed.includes('instagram.com') &&
        (trimmed.includes('/reel/') || trimmed.includes('/reels/') || trimmed.includes('/p/'))
      ) {
        kind = 'instagram';
        console.log(`📸 Instagram URL detected, calling cleanUrl: ${trimmed}`);
        try { normalized = cleanUrl(trimmed); } catch (err) {
          console.error('cleanUrl error:', err);
          /* keep raw */
        }
      }
      const isInternalDuplicate = seenInThisInput.has(normalized);
      if (!isInternalDuplicate) seenInThisInput.add(normalized);
      const isExistingDuplicate = existingCreativesForRelease.has(normalized) || existingCreativesForRelease.has(trimmed);
      
      console.log(`🔍 Checking URL: ${trimmed}`);
      console.log(`   - Normalized: ${normalized}`);
      console.log(`   - Has raw in set: ${existingCreativesForRelease.has(trimmed)}`);
      console.log(`   - Has normalized in set: ${existingCreativesForRelease.has(normalized)}`);
      
      if (isExistingDuplicate) {
        console.log(`⚠️ Per-line duplicate detected: ${trimmed}`);
        console.log(`   - Normalized: ${normalized}`);
        console.log(`   - Has raw: ${existingCreativesForRelease.has(trimmed)}`);
        console.log(`   - Has normalized: ${existingCreativesForRelease.has(normalized)}`);
      }
      
      return {
        raw: trimmed,
        normalized,
        kind,
        isDuplicate: isInternalDuplicate || isExistingDuplicate,
        isExistingDuplicate,
        isInternalDuplicate,
      };
    });
  }, [socialMediaLines, existingCreativesForRelease]);

  const socialMediaDuplicateCount = socialMediaLineStatus.filter((s) => s.isDuplicate).length;

  const validateUrls = (platform: 'instagram' | 'tiktok' | 'dropbox', urls: string) => {
    const lines = urls.split('\n').filter(url => url.trim());
    const duplicates: string[] = [];
    const invalidFormats: string[] = [];
    
    // Check for duplicates within current submission
    const uniqueUrls = new Set<string>();
    const internalDuplicates: string[] = [];

    for (const url of lines) {
      const cleanedUrl = cleanUrl(url);

      // Check URL format
      if (platform === 'instagram' && 
          !(cleanedUrl.includes('instagram.com/reel') || 
            cleanedUrl.includes('instagram.com/reels') ||
            cleanedUrl.includes('instagram.com/p/') || 
            /instagram\.com\/[^\/]+\/reels?\//.test(cleanedUrl) ||
            /instagram\.com\/[^\/]+\/p\//.test(cleanedUrl))) {
        invalidFormats.push(url.trim());
        continue;
      }
      if (platform === 'dropbox') {
        if (!cleanedUrl.includes('dropbox.com') && !isSupabaseStorageUrl(cleanedUrl)) {
          invalidFormats.push(url.trim());
          continue;
        }
        // Validate that it's a proper URL
        try {
          new URL(cleanedUrl);
        } catch {
          invalidFormats.push(url.trim());
          continue;
        }
      }
      if (platform === 'instagram') {
        // Validate that it's a proper URL
        try {
          new URL(cleanedUrl);
        } catch {
          invalidFormats.push(url.trim());
          continue;
        }
      }
      if (platform === 'tiktok' && !cleanedUrl.startsWith('#')) {
        invalidFormats.push(url.trim());
        continue;
      }

      // Check for duplicates in existing ad creatives — release-scoped.
      // The same URL/code can be submitted to a different release.
      const isDuplicate = adCreatives.some(creative => 
        (creative as any).release_id === selectedRelease && (
          creative.content === cleanedUrl ||
          (platform === 'instagram' && creative.mergedInstagramReelUrl === cleanedUrl) ||
          (platform === 'tiktok' && creative.mergedTiktokAuthCode === cleanedUrl)
        )
      );

      if (isDuplicate) {
        duplicates.push(url.trim());
        continue; // Skip duplicates but don't block submission
      }

      // Check for duplicates within current submission
      if (uniqueUrls.has(cleanedUrl)) {
        internalDuplicates.push(url.trim());
      } else {
        uniqueUrls.add(cleanedUrl);
      }
    }

    // Only throw error for invalid formats (blocking error)
    if (invalidFormats.length > 0) {
      throw new Error(`Invalid format for the following URLs:\n${invalidFormats.join('\n')}`);
    }

    // Show warnings for duplicates but don't block submission
    const warnings: string[] = [];
    if (duplicates.length > 0) {
      warnings.push(`Skipped ${duplicates.length} already submitted URL(s)`);
    }
    if (internalDuplicates.length > 0) {
      warnings.push(`Skipped ${internalDuplicates.length} duplicate URL(s) in your submission`);
    }
    if (warnings.length > 0) {
      console.log('Validation warnings:', warnings.join(', '));
    }

    // If all URLs were duplicates, throw an error
    if (uniqueUrls.size === 0) {
      throw new Error('All URLs have already been submitted or are duplicates');
    }

    return Array.from(uniqueUrls);
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      // Simulate the file input change event
      const fileList = e.dataTransfer.files;
      const event = { target: { files: fileList } } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleFileChange(event);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setError(null);
    setUploadLogs([]);
    setTotalFilesToUpload(files.length);
    setCurrentFileIndex(0);
    
    // Calculate total file size
    const totalSize = Array.from(files).reduce((sum, file) => sum + file.size, 0);
    setUploadStats({
      remainingTime: 0,
      uploadSpeed: 0,
      fileSize: formatFileSize(totalSize)
    });
    
    // Add initial log
    setUploadLogs([{ message: `Starting upload of ${files.length} file${files.length > 1 ? 's' : ''} (${formatFileSize(totalSize)})`, type: 'info' }]);

    // Process each file sequentially
    const uploadFiles = async () => {
      const newUrls: string[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setCurrentFileIndex(i + 1);
        setCurrentFileName(file.name);
        
        const fileSizeStr = formatFileSize(file.size);
        setUploadLogs(prev => [...prev, { message: `[${i + 1}/${files.length}] Validating: ${file.name} (${fileSizeStr})`, type: 'info' }]);

        // Validate video dimensions and aspect ratio
        const validation = await validateVideoFile(file);
        if (!validation.valid) {
          setUploadLogs(prev => [...prev, { message: `[${i + 1}/${files.length}] Skipped: ${file.name} - ${validation.error}`, type: 'error' }]);
          setError(`${file.name}: ${validation.error}`);
          continue;
        }

        // Aspect ratio / resolution warning:
        //   - Admin: skip entirely, upload anyway
        //   - Artist: show confirmation prompt, upload on confirm
        if (validation.warning && !isAdmin) {
          const confirmed = window.confirm(
            `${file.name}: ${validation.warning}\n\nThis may not display correctly as an ad. Upload anyway?`
          );
          if (!confirmed) {
            setUploadLogs(prev => [...prev, { message: `[${i + 1}/${files.length}] Skipped: ${file.name} - user cancelled`, type: 'error' }]);
            continue;
          }
        } else if (validation.warning && isAdmin) {
          setUploadLogs(prev => [...prev, { message: `[${i + 1}/${files.length}] Warning (admin, uploading anyway): ${file.name} - ${validation.warning}`, type: 'info' }]);
        }
        
        setUploadLogs(prev => [...prev, { message: `[${i + 1}/${files.length}] Uploading: ${file.name} (${fileSizeStr})`, type: 'progress' }]);
        
        try {
          const { url, fileName } = await new Promise<{ url: string; fileName: string }>((resolve, reject) => {
            const uploader = new VideoUploader({
              onStart: () => {
                setUploadProgress(0);
              },
              onProgress: (progress, remainingTime = 0, uploadSpeed = 0) => {
                setUploadProgress(progress);
                setUploadStats(prev => prev ? {
                  ...prev,
                  remainingTime,
                  uploadSpeed,
                  fileSize: fileSizeStr
                } : null);
              },
              onUploadComplete: (url, originalFileName) => {
                resolve({ url, fileName: originalFileName || file.name.replace(/\.[^/.]+$/, '') });
              },
              onError: (error) => {
                reject(new Error(error));
              }
            });
            
            uploader.uploadFile(file).catch(reject);
          });
          
          newUrls.push(url);
          // Track the original filename for this URL (auto-filled from file name)
          setUploadedFileNames(prev => ({ ...prev, [url]: fileName }));
          setUploadLogs(prev => [...prev, { message: `[${i + 1}/${files.length}] Uploaded: ${file.name}`, type: 'success' }]);
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
          setUploadLogs(prev => [...prev, { message: `[${i + 1}/${files.length}] Failed: ${file.name} - ${error instanceof Error ? error.message : 'Unknown error'}`, type: 'error' }]);
          setError(`Error uploading ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }
      
      setUploadedUrls(prev => [...prev, ...newUrls]);
      setIsUploading(false);
      setUploadStats(null);
      setCurrentFileName('');
      setUploadLogs(prev => [...prev, { message: `Completed! ${newUrls.length}/${files.length} files uploaded successfully`, type: newUrls.length === files.length ? 'success' : 'info' }]);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    
    uploadFiles().catch(error => {
      console.error('Error in file upload process:', error);
      setIsUploading(false);
      setUploadStats(null);
      setError('Failed to upload files: ' + (error instanceof Error ? error.message : 'Unknown error'));
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds) || seconds <= 0) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatSpeed = (bytesPerSecond: number): string => {
    if (bytesPerSecond === 0) return '0 KB/s';
    const k = 1024;
    const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
    const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
    return parseFloat((bytesPerSecond / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Validate video dimensions and aspect ratio
  // Returns { valid, warning?, error? }:
  //   - valid=true, no warning → all good
  //   - valid=true, warning set → aspect ratio/resolution doesn't match
  //     recommendations but upload is allowed (admin skips entirely,
  //     artist gets a confirmation prompt)
  //   - valid=false, error set → unrecoverable (metadata read failure)
  const validateVideoFile = async (file: File): Promise<{ valid: boolean; warning?: string; error?: string }> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        const width = video.videoWidth;
        const height = video.videoHeight;

        // Calculate aspect ratio
        const aspectRatio = width / height;

        // Check for 9:16 (0.5625) or 16:9 (1.7778) aspect ratios with small tolerance
        const is9to16 = Math.abs(aspectRatio - 0.5625) < 0.01;
        const is16to9 = Math.abs(aspectRatio - 1.7778) < 0.01;

        const warnings: string[] = [];

        if (!is9to16 && !is16to9) {
          warnings.push(`not 9:16 or 16:9 (current: ${width}x${height}, ${aspectRatio.toFixed(2)}:1)`);
        }

        // Check minimum resolution
        // For 9:16: minimum 1080x1920
        // For 16:9: minimum 1920x1080
        if (is9to16 && (width < 1080 || height < 1920)) {
          warnings.push(`9:16 resolution below 1080x1920 (current: ${width}x${height})`);
        }

        if (is16to9 && (width < 1920 || height < 1080)) {
          warnings.push(`16:9 resolution below 1920x1080 (current: ${width}x${height})`);
        }

        URL.revokeObjectURL(video.src);
        resolve({
          valid: true,
          warning: warnings.length > 0 ? `Video is ${warnings.join(', ')}.` : undefined,
        });
      };

      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        resolve({
          valid: false,
          error: 'Failed to read video metadata. Please try a different file.'
        });
      };

      video.src = URL.createObjectURL(file);
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!socialMediaUrls.trim() && !dropboxUrls.trim() && uploadedUrls.length === 0) {
      setError('Please enter at least one URL or code');
      return;
    }

    if (!artistId && !selectedArtist) {
      setError('Please select an artist');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      type AdCreativeInsert = {
        artists_id: string;
        platform: string;
        content: string;
        status: 'pending';
        submission_id?: string | null;
        video_name?: string | null;
        thumbnail_url?: string | null;
        release_id?: string | null;
      };

      // Save selected release to session storage
      if (selectedRelease && selectedArtist) {
        sessionStorage.setItem(`selected_release_${selectedArtist}`, selectedRelease);
      }

      let entries: AdCreativeInsert[] = [];

      // Process Social Media URLs (Instagram and TikTok)
      if (socialMediaUrls.trim()) {
        // Split by line and inline https:// boundaries (handles concatenated URLs)
        const lines = splitSocialMediaInput(socialMediaUrls);

        for (const line of lines) {
          const trimmedLine = line.trim();

          // Detect if it's Instagram or TikTok
          if (trimmedLine.startsWith('#')) {
            // TikTok AUTH code
            try {
              const validTiktokUrls = validateUrls('tiktok', trimmedLine);
              entries.push(...validTiktokUrls.map(url => ({
                artists_id: artistId || selectedArtist,
                platform: 'tiktok',
                content: url,
                status: 'pending' as const,
                release_id: selectedRelease || null
              })));
            } catch (err) {
              console.error('Error validating TikTok code:', err);
              throw err;
            }
          } else if (trimmedLine.includes('instagram.com') && 
                    (trimmedLine.includes('/reel/') || trimmedLine.includes('/reels/') || trimmedLine.includes('/p/'))) {
            // Instagram URL
            try {
              const validInstagramUrls = validateUrls('instagram', trimmedLine);
              entries.push(...validInstagramUrls.map(url => ({
                artists_id: artistId || selectedArtist,
                platform: 'instagram',
                content: url,
                status: 'pending' as const,
                release_id: selectedRelease || null
              })));
              
              // Try to fetch Instagram thumbnails
              for (const url of validInstagramUrls) {
                try {
                  fetchInstagramThumbnail(url, null).catch(console.error);
                } catch (thumbnailError) {
                  console.error('Error fetching Instagram thumbnail:', thumbnailError);
                }
              }
            } catch (err) {
              console.error('Error validating Instagram URL:', err);
              throw err;
            }
          } else {
            // Unknown format
            throw new Error(`Unrecognized format: "${trimmedLine}". Please enter either Instagram URLs or TikTok AUTH codes (starting with #).`);
          }
        }
      }
      
      // Process Dropbox URLs
      if (dropboxUrls.trim() || uploadedUrls.length > 0) {
        // Combine manually entered Dropbox URLs with uploaded URLs
        const validDropboxUrls = dropboxUrls.trim() ? validateUrls('dropbox', dropboxUrls) : [];
        const validUploadedUrls = uploadedUrls.length > 0 ? validateUrls('dropbox', uploadedUrls.join('\n')) : [];
        const allValidUrls = [...validDropboxUrls, ...validUploadedUrls];
        entries.push(...allValidUrls.map(url => {
          // Determine if this is a direct upload or dropbox URL
          const platform = isSupabaseStorageUrl(url) ? 'direct_upload' : 'dropbox';
          
          // Generate thumbnail for video URLs
          const thumbnailUrl = platform === 'direct_upload' ? thumbnails[url] || null : null;
          
          // Check if this URL matches a submission
          const matchingSubmission = submissions.find(sub => sub.videoUrl === url);
          
          return {
            artists_id: artistId || selectedArtist,
            platform,
            content: url,
            submission_id: matchingSubmission?.id?.toString() || null,
            video_name: matchingSubmission?.projectName || uploadedFileNames[url] || (platform === 'dropbox' ? extractDropboxFileName(url) : null), 
            status: 'pending' as const,
            thumbnail_url: thumbnailUrl || null,
            release_id: selectedRelease || null
          };
        }));
      }

      if (entries.length === 0) {
        throw new Error('No valid URLs to submit');
      }

      // Filter out duplicates already known to exist for this release (matches
      // content, merged_instagram_reel_url, merged_tiktok_auth_code, or
      // original_instagram_url — covers licensed-replaced rows).
      // Check both the raw content and its normalized form.
      console.log('🔍 Duplicate detection - existingCreativesForRelease size:', existingCreativesForRelease.size);
      console.log('🔍 Duplicate detection - entries before filter:', entries.map(e => e.content));
      
      const skippedDuplicateCount = entries.filter((e) => {
        const normalized = e.content.includes('instagram.com') ? cleanUrl(e.content) : e.content;
        const isDuplicate = existingCreativesForRelease.has(e.content) || existingCreativesForRelease.has(normalized);
        if (isDuplicate) {
          console.log(`⚠️ Duplicate detected: ${e.content}`);
          console.log(`   - Normalized: ${normalized}`);
          console.log(`   - Has raw: ${existingCreativesForRelease.has(e.content)}`);
          console.log(`   - Has normalized: ${existingCreativesForRelease.has(normalized)}`);
        }
        return isDuplicate;
      }).length;
      
      if (skippedDuplicateCount > 0) {
        const before = entries.length;
        entries = entries.filter((e) => {
          const normalized = e.content.includes('instagram.com') ? cleanUrl(e.content) : e.content;
          return !existingCreativesForRelease.has(e.content) && !existingCreativesForRelease.has(normalized);
        });
        console.log(`Skipped ${before - entries.length} duplicate(s) already on this release`);
        console.log('🔍 Entries after duplicate filter:', entries.map(e => e.content));
      }
      if (entries.length === 0) {
        throw new Error('All provided URLs already exist for this release.');
      }

      // Check for duplicates in database before attempting insert (scoped to the
      // selected release — same URL is allowed across different releases).
      const urlsToCheck = entries.map(e => e.content);
      let dupQuery = db
        .from('ad_creatives')
        .select('content')
        .in('content' as any, urlsToCheck as any);
      if (selectedRelease) {
        dupQuery = dupQuery.eq('release_id' as any, selectedRelease as any);
      }
      const { data: existingCreatives, error: checkError } = await dupQuery;

      if (checkError) {
        console.error('Error checking for duplicates:', checkError);
      } else if (existingCreatives && existingCreatives.length > 0) {
        const duplicateUrls = (existingCreatives as any[]).map((c: any) => c.content);
        const duplicateCount = duplicateUrls.length;
        
        // Filter out duplicates from entries
        const filteredEntries = entries.filter(e => !duplicateUrls.includes(e.content));
        
        if (filteredEntries.length === 0) {
          throw new Error(`All ${duplicateCount} URL(s) have already been submitted to this release. Please check your URLs.`);
        }
        
        // Update entries to only include non-duplicates
        entries.length = 0;
        entries.push(...filteredEntries);
        
        // Show warning about skipped duplicates
        console.warn(`⚠️ Skipped ${duplicateCount} duplicate URL(s) that already exist in the database`);
        setError(`Note: ${duplicateCount} duplicate URL(s) were skipped. Submitting ${filteredEntries.length} new URL(s).`);
      }

      // Create ad creatives by inserting directly to database
      const insertData = entries.map(entry => ({
        artists_id: entry.artists_id,
        platform: entry.platform,
        content: entry.content,
        status: entry.status,
        thumbnail_url: entry.thumbnail_url,
        submission_id: entry.submission_id,
        video_name: entry.video_name,
        release_id: entry.release_id
      }));

      const { data: insertedRows, error } = await db
        .from('ad_creatives')
        .insert(insertData as any)
        .select('*, releases(id, name, release_date, meta_campaign_id, meta_campaign_name)');

      if (error) {
        // Check if it's a unique constraint violation
        if (error.code === '23505' || error.message?.includes('duplicate key') || error.message?.includes('unique constraint')) {
          throw new Error('One or more URLs have already been submitted to the system. Please check your URLs and remove any that were previously submitted.');
        }
        throw error;
      }

      // Fire-and-forget audio scan for each newly inserted Instagram creative.
      // If the reel uses licensed music, the backend will download the video
      // and overwrite the row's content + merged_instagram_reel_url.
      try {
        const instagramRows = (insertedRows || []).filter(
          (row: any) => row?.platform === 'instagram' && row?.content?.includes('instagram.com')
        );
        if (instagramRows.length > 0) {
          Promise.allSettled(
            instagramRows.map((row: any) =>
              scanInstagramAudio(row.id, row.content)
            )
          ).then(() => {
            // Refresh so UI reflects any licensed-music replacements.
            fetchAdCreatives(1, 100, { artistId }, true, artistClient || undefined).catch(console.error);
          });
        }
      } catch (scanErr) {
        console.error('Audio scan dispatch error:', scanErr);
      }

      // Fire-and-forget TikTok Spark Ad poster fetch for each newly inserted
      // TikTok auth-code row. The reporting-project edge function authorizes
      // the auth_code, calls tt_video/info, downloads the poster from the
      // short-lived TikTok CDN and permanently mirrors it into our
      // `thumbnails` bucket + writes `tiktok_thumbnail_url`.
      try {
        const tiktokRows = (insertedRows || []).filter(
          (row: any) =>
            row?.platform === 'tiktok' &&
            typeof row?.content === 'string' &&
            row.content.startsWith('#')
        );
        if (tiktokRows.length > 0) {
          const REPORTING_URL = 'https://uydhsjvwrgupgfjevqsz.supabase.co';
          const REPORTING_ANON =
            'sb_publishable_BrCqK-o8PBNyoNFYpxWoxg_qDwheRR5';
          Promise.allSettled(
            tiktokRows.map((row: any) =>
              fetch(`${REPORTING_URL}/functions/v1/download-tiktok-spark-ad-media`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${REPORTING_ANON}`,
                },
                body: JSON.stringify({ creative_id: row.id, poster_only: true }),
              })
            )
          ).then(() => {
            // Refresh so the UI renders the freshly stored posters.
            fetchAdCreatives(1, 100, { artistId }, true, artistClient || undefined).catch(console.error);
          });
        }
      } catch (ttErr) {
        console.error('TikTok poster fetch dispatch error:', ttErr);
      }

      // Refresh ad creatives to show the new entries with proper release data
      await fetchAdCreatives(1, 100, { artistId }, true, artistClient || undefined);
      
      // Send WhatsApp notification to ad creative group
      try {
        const selectedArtistId = artistId || selectedArtist;
        const artist = artists.find(a => a.id === selectedArtistId);
        
        if (artist) {
          // Count creatives by platform
          const platformCounts = new Map<string, number>();
          entries.forEach(entry => {
            platformCounts.set(entry.platform, (platformCounts.get(entry.platform) || 0) + 1);
          });
          
          const platforms = Array.from(platformCounts.entries()).map(([platform, count]) => ({
            platform,
            count
          }));
          
          // Import WhatsAppService dynamically
          const { WhatsAppService } = await import('../services/whatsapp');
          
          // Send notification
          await WhatsAppService.notifyAdCreativeSubmission({
            artistName: artist.name,
            artistId: selectedArtistId,
            platforms,
            totalCount: entries.length
          });
          
          console.log('✅ WhatsApp notification sent for new ad creative submission');
        }
      } catch (notifyError) {
        console.error('❌ Error sending WhatsApp notification:', notifyError);
        // Don't fail the submission if notification fails
      }
      
      onClose();
    } catch (err) {
      console.error('Error submitting URLs:', err);
      if (err instanceof Error && err.message === 'duplicate_entry') {
        setError('One or more of the URLs you tried to submit already exist in the system. Please check your URLs and remove any duplicates.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to submit URLs');
      }
    } finally {
      setLoading(false);
    }
  };

  // --- Per-release cover + Spotify URI ---
  // Section shows while the release is missing either asset (and briefly after
  // a cover upload so the mini preview is visible). Once both are saved it
  // disappears entirely for that release.
  const selectedReleaseObj = releases.find(r => r.id === selectedRelease);
  const releaseCoverUrl = selectedReleaseObj?.cover_url || null;
  const releaseHasUri = !!selectedReleaseObj?.spotify_uri;
  const coverJustUploadedForThis = coverJustUploaded === selectedRelease;
  // From the release date onward the section is never shown again,
  // even if assets are still missing — it's a pre-release reminder only.
  const releaseIsPast = !!selectedReleaseObj?.release_date &&
    new Date(selectedReleaseObj.release_date) <= new Date(new Date().toDateString());
  const showReleaseAssets = !!selectedReleaseObj && !releaseIsPast &&
    (!releaseCoverUrl || !releaseHasUri || coverJustUploadedForThis);
  const releaseIsThisWeek = !!selectedReleaseObj &&
    isReleaseInCurrentWeek(selectedReleaseObj.release_date);

  // Keep the URI draft in sync with whichever release is selected.
  useEffect(() => {
    setUriDraft(selectedReleaseObj?.spotify_uri || '');
    setUriError(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRelease]);

  const patchRelease = (releaseId: string, patch: Record<string, any>) =>
    setReleases(prev => prev.map(r => r.id === releaseId ? { ...r, ...patch } : r));

  const handleReleaseCoverPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (coverInputRef.current) coverInputRef.current.value = '';
    if (!file || !selectedRelease) return;
    setUploadingCover(true);
    setError(null);
    try {
      const url = await uploadReleaseCover(db, selectedRelease, file);
      patchRelease(selectedRelease, { cover_url: url });
      setCoverJustUploaded(selectedRelease);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cover upload failed');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSaveReleaseUri = async () => {
    const normalized = normalizeSpotifyUri(uriDraft);
    if (!normalized) {
      setUriError('Invalid Spotify URI — expected e.g. spotify:album:4itKk52E9ZCdWUQcFAkud9');
      return;
    }
    setSavingUri(true);
    setUriError(null);
    try {
      const { error: updateError } = await db
        .from('releases')
        .update({ spotify_uri: normalized })
        .eq('id', selectedRelease);
      if (updateError) throw updateError;
      patchRelease(selectedRelease, { spotify_uri: normalized });
    } catch (err) {
      setUriError(err instanceof Error ? err.message : 'Saving failed');
    } finally {
      setSavingUri(false);
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: {
        type: "spring",
        duration: 0.5,
        bounce: 0.3
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9, 
      y: 20, 
      transition: {
        duration: 0.2
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <motion.div
        className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto p-4 sm:p-5"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold dark:text-white">Add URLs</h2>
          <motion.button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 p-2 -mr-2 touch-manipulation"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <X className="h-5 w-5" />
          </motion.button>
        </div>

        <AnimatePresence>
          {error && (
            <motion.div 
              className="mb-4 flex items-start gap-2 text-red-500 bg-red-50 dark:bg-red-900/20 p-3 rounded-md whitespace-pre-wrap"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p>{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Artist Selection - Only show if no artistId prop */}
          {!artistId && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <label className="block text-sm font-medium mb-1 dark:text-white">
                Artist <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedArtist}
                onChange={(e) => setSelectedArtist(e.target.value)}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              >
                <option value="">Select an artist</option>
                {activeArtists.map((artist) => (
                  <option key={artist.id} value={artist.id}>
                    {artist.name}
                  </option>
                ))}
              </select>
            </motion.div>
          )}

          {/* Release Selection — kept at the top of the modal (same position
              and styling as the Submit New Video modal) so the target release
              is chosen before any URLs or files are added. Always rendered
              once an artist is known, even when the artist has no releases. */}
          {selectedArtist && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.05 }}
            >
              <label className="block text-sm font-medium mb-1 dark:text-white">
                Release
              </label>
              <select
                value={selectedRelease}
                onChange={(e) => setSelectedRelease(e.target.value)}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="">No Release Assigned</option>
                {releases.map((release: any) => (
                  <option key={release.id} value={release.id}>
                    {release.name}{release.release_date ? ` (${new Date(release.release_date).toLocaleDateString('de-DE', { day: 'numeric', month: 'numeric', year: 'numeric' })})` : ''}
                  </option>
                ))}
              </select>
            </motion.div>
          )}

          {/* Release assets: cover + Spotify URI — hides itself once the release has both */}
          {showReleaseAssets && selectedReleaseObj && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-md p-3 space-y-3 ${
                releaseIsThisWeek
                  ? 'border-2 border-yellow-400 bg-yellow-50 dark:bg-yellow-900/10'
                  : 'border border-gray-200 dark:border-gray-600'
              }`}
            >
              {releaseIsThisWeek && (
                <p className="text-sm font-medium text-yellow-700 dark:text-yellow-300">
                  This release is out this week — please upload the cover & Spotify URI.
                </p>
              )}
              <div className="flex items-start gap-3">
                {/* Cover cell */}
                <div className="shrink-0">
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/*,.heic,.heif"
                    className="hidden"
                    onChange={handleReleaseCoverPick}
                  />
                  {releaseCoverUrl ? (
                    <button
                      type="button"
                      onClick={() => coverInputRef.current?.click()}
                      disabled={uploadingCover}
                      className="block w-16 h-16 rounded-md overflow-hidden border border-gray-200 dark:border-gray-600"
                      title="Replace cover"
                    >
                      <img src={releaseCoverUrl} alt="Cover" className="w-full h-full object-cover" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => coverInputRef.current?.click()}
                      disabled={uploadingCover}
                      className="w-16 h-16 rounded-md border-2 border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center gap-1 text-gray-400 hover:border-primary-500 hover:text-primary-500 transition-colors disabled:opacity-50"
                      title="Upload cover"
                    >
                      {uploadingCover ? <Loader className="h-4 w-4 animate-spin" /> : <Image className="h-4 w-4" />}
                      <span className="text-[10px]">{uploadingCover ? '…' : 'Cover'}</span>
                    </button>
                  )}
                </div>

                {/* URI cell — hidden once the release has one */}
                {!releaseHasUri && (
                  <div className="flex-1 min-w-0">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={uriDraft}
                        onChange={(e) => { setUriDraft(e.target.value); setUriError(null); }}
                        placeholder="spotify:album:4itKk52E9ZCdWUQcFAkud9"
                        className="flex-1 px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white font-mono focus:border-primary-500 focus:ring-primary-500"
                      />
                      <button
                        type="button"
                        onClick={handleSaveReleaseUri}
                        disabled={savingUri || !uriDraft.trim()}
                        className="px-3 py-2 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-40"
                      >
                        {savingUri ? <Loader className="h-4 w-4 animate-spin" /> : 'Save'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Guide: <a href="https://swipe.fm/spotify-uri" target="_blank" rel="noreferrer" className="text-primary-500 hover:underline">swipe.fm/spotify-uri</a>
                    </p>
                    {uriError && <p className="text-xs text-red-500 mt-1">{uriError}</p>}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Instagram Reels Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div className="flex items-center gap-2 mb-2 justify-between">
              <div className="flex items-center gap-2">
                <div className="flex space-x-1">
                  <Instagram className="h-5 w-5 text-white z-10" />
                  <svg 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    className="h-5 w-5 text-white"
                  >
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </div>
                <span className="text-sm font-medium dark:text-white">
                  Instagram & TikTok
                </span>
                {igPostsLoading && isAdmin && selectedArtist && (
                  <LoaderIcon className="h-3.5 w-3.5 text-gray-400 animate-spin ml-1" />
                )}
              </div>
              <div className="flex gap-2">
                {instagramCount > 0 && (
                  <span className="text-xs bg-pink-100 text-pink-800 dark:bg-pink-900/20 dark:text-pink-200 px-2 py-0.5 rounded-full flex items-center">
                    <Instagram className="h-3 w-3 mr-1" />
                    {instagramCount}
                  </span>
                )}
                {tiktokCount > 0 && (
                  <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200 px-2 py-0.5 rounded-full flex items-center">
                    <svg 
                      viewBox="0 0 24 24" 
                      fill="currentColor"
                      className="h-3 w-3 mr-1"
                    >
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                    {tiktokCount}
                  </span>
                )}
              </div>
            </div>
            
            {/* Recent IG posts from the artist's connected account — admin only */}
            {/* Only show after loading completes (spinner is in the header instead).
                Hidden entirely when there are no posts (e.g. no linked Meta
                campaign / IG account) so the error text never shows. */}
            {selectedArtist && isAdmin && !igPostsLoading && igRecentPosts.length > 0 && (
              <div className="mb-3 rounded-md border border-gray-200 dark:border-gray-600 p-3">
                <button
                  type="button"
                  onClick={() => setShowIgPosts(!showIgPosts)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors w-full"
                >
                  <Instagram className="h-4 w-4" />
                  <span>
                    Recent posts from @{igUsername} ({igRecentPosts.length})
                    <span className="ml-1 text-gray-400">{showIgPosts ? '▾' : '▸'}</span>
                  </span>
                </button>
                {showIgPosts && !igPostsLoading && igRecentPosts.length > 0 && (
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[324px] overflow-y-auto">
                    {igRecentPosts.map((post) => {
                      const isInTextarea = socialMediaUrls.includes(post.permalink);
                      const date = post.timestamp ? new Date(post.timestamp) : null;
                      const dateStr = date
                        ? `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}`
                        : '';
                      // Check if this post is already in the ad_creatives table for this release
                      let existingEntry: { status: string; meta_ad_status: string | null } | undefined;
                      try {
                        const cleaned = cleanUrl(post.permalink);
                        existingEntry = existingCreativesForRelease.get(post.permalink) ||
                          (cleaned ? existingCreativesForRelease.get(cleaned) : undefined);
                      } catch { /* ignore */ }
                      const isAlreadyAdded = !!existingEntry;
                      const adStatus = existingEntry?.meta_ad_status || existingEntry?.status;
                      // Status badge color
                      const statusColor = adStatus === 'ACTIVE' ? 'bg-green-500' :
                        adStatus === 'PAUSED' ? 'bg-yellow-500' :
                        adStatus === 'REJECTED' || adStatus === 'DISAPPROVED' ? 'bg-red-500' :
                        'bg-gray-500';
                      const statusLabel = adStatus === 'ACTIVE' ? 'Active' :
                        adStatus === 'PAUSED' ? 'Paused' :
                        adStatus === 'REJECTED' || adStatus === 'DISAPPROVED' ? 'Rejected' :
                        adStatus === 'PENDING_REVIEW' ? 'Review' :
                        adStatus ? adStatus : 'Pending';
                      const isDisabled = isInTextarea || isAlreadyAdded;
                      const audioScan = igAudioScan[post.permalink];
                      const audioLoading = audioScan?.loading;
                      const isOriginal = audioScan?.audio_type === 'original_sounds';
                      return (
                        <button
                          key={post.id}
                          type="button"
                          onClick={() => {
                            if (isDisabled) return;
                            const newUrl = post.permalink;
                            setSocialMediaUrls(prev =>
                              prev.trim()
                                ? `${prev.trim()}\n${newUrl}`
                                : newUrl
                            );
                          }}
                          disabled={isDisabled}
                          className={`relative group rounded-md overflow-hidden border-2 transition-all ${
                            isAlreadyAdded
                              ? 'border-gray-400 dark:border-gray-500 cursor-default'
                              : isInTextarea
                                ? 'border-green-500 cursor-default'
                                : 'border-transparent hover:border-blue-500 cursor-pointer'
                          }`}
                          title={post.caption || post.permalink}
                        >
                          {post.thumbnail ? (
                            <img
                              src={post.thumbnail}
                              alt={post.caption || 'IG post'}
                              className="aspect-[9/16] w-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="aspect-[9/16] w-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                              <Instagram className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                          {post.media_type === 'VIDEO' && (
                            <div className="absolute top-1 right-1 bg-black/60 rounded px-1 py-0.5">
                              <svg viewBox="0 0 24 24" fill="white" className="h-3 w-3">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          )}
                          {dateStr && (
                            <div className="absolute top-1 left-1 bg-black/60 text-white text-[9px] px-1 py-0.5 rounded font-mono leading-none">
                              {dateStr}
                            </div>
                          )}
                          {/* Audio scan badges */}
                          {audioLoading && (
                            <div className="absolute top-1 right-7 bg-black/60 rounded px-1 py-0.5">
                              <LoaderIcon className="h-3 w-3 text-white animate-spin" />
                            </div>
                          )}
                          {/* Licensed audio badge hidden — reels are promoted as regular IG posts regardless of audio type */}
                          {isOriginal && !isAlreadyAdded && (
                            <div className="absolute bottom-6 left-1 right-1 bg-green-500/80 text-white text-[9px] px-1 py-0.5 rounded text-center font-medium leading-tight">
                              ✓ Original
                            </div>
                          )}
                          {isAlreadyAdded && (
                            <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-1">
                              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="h-5 w-5 drop-shadow-lg">
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                              <span className={`text-[9px] text-white px-1.5 py-0.5 rounded font-medium leading-none ${statusColor}`}>
                                {statusLabel}
                              </span>
                            </div>
                          )}
                          {isInTextarea && !isAlreadyAdded && (
                            <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" className="h-6 w-6 drop-shadow-lg">
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                            </div>
                          )}
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                            {post.caption || 'No caption'}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {showIgPosts && !igPostsLoading && igRecentPosts.length > 0 && (
                  <p className="text-[10px] text-gray-400 mt-1.5">
                    Click a post to add its URL to the textarea
                  </p>
                )}
              </div>
            )}

            <textarea
              value={socialMediaUrls}
              onChange={(e) => setSocialMediaUrls(e.target.value)}
              placeholder="Enter Instagram Reel URLs or TikTok AUTH Codes (one per line)"
              className="w-full h-32 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-none"
              disabled={loading}
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <p>Example: https://www.instagram.com/reel/...</p>
              <p>Example: #7253947593475934...</p>
            </div>

            {/* Per-line duplicate / status preview */}
            {selectedArtist && socialMediaLineStatus.length > 0 && (
              <div className="mt-2 space-y-1 text-xs">
                {socialMediaDuplicateCount > 0 && (
                  <p className="text-amber-600 dark:text-amber-400">
                    {socialMediaDuplicateCount} duplicate{socialMediaDuplicateCount === 1 ? '' : 's'} detected for this release — will not be added.
                  </p>
                )}
                <ul className="rounded-md border border-gray-200 dark:border-gray-600 divide-y divide-gray-200 dark:divide-gray-600 max-h-40 overflow-y-auto">
                  {socialMediaLineStatus.map((line, idx) => (
                    <li
                      key={`${line.normalized}-${idx}`}
                      className={`flex items-center justify-between gap-2 px-2 py-1 ${
                        line.isDuplicate
                          ? 'bg-amber-50 dark:bg-amber-900/20'
                          : 'bg-white dark:bg-gray-800'
                      }`}
                    >
                      <span className="truncate font-mono text-gray-700 dark:text-gray-200" title={line.raw}>
                        {line.raw}
                      </span>
                      {line.isDuplicate ? (
                        <span className="shrink-0 text-[10px] uppercase tracking-wide font-medium text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded">
                          {line.isInternalDuplicate && !line.isExistingDuplicate
                            ? 'duplicate in list — will not be added'
                            : 'duplicate, will not be added'}
                        </span>
                      ) : (
                        <span className="shrink-0 text-[10px] uppercase tracking-wide font-medium text-green-600 dark:text-green-400">
                          new
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>


          {/* Direct Video Upload Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <div className="flex items-center gap-2 mb-2 justify-between">
              <div className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-white dark:text-white" />
                <span className="text-sm font-medium dark:text-white">
                  Direct Video Upload
                </span>
              </div>
              {uploadedUrls.length > 0 && (
                <span className="text-xs bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200 px-2 py-0.5 rounded-full flex items-center">
                  <Upload className="h-3 w-3 mr-1" />
                  {uploadedUrls.length}
                </span>
              )}
            </div>
            <div className="space-y-2">
              {/* Drag & Drop Zone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleFileSelect}
                className={`flex items-center justify-center gap-2 px-3 py-4 rounded-md border-2 border-dashed cursor-pointer transition-colors ${
                  isDragOver
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                } ${isUploading || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isUploading ? (
                  <>
                    <LoaderIcon className="h-4 w-4 animate-spin" />
                    <div className="flex flex-col items-center">
                      <span>Uploading [{currentFileIndex}/{totalFilesToUpload}] {Math.round(uploadProgress)}%</span>
                      {currentFileName && (
                        <div className="text-xs text-gray-400 mt-0.5 max-w-[200px] truncate">{currentFileName}</div>
                      )}
                      {uploadStats && (
                        <div className="text-xs text-gray-500 mt-1 flex gap-2">
                          <span>{uploadStats.fileSize}</span>
                          {uploadStats.uploadSpeed > 0 && <span>{formatSpeed(uploadStats.uploadSpeed)}</span>}
                          {uploadStats.remainingTime > 0 && <span>{formatTime(uploadStats.remainingTime)}</span>}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <Upload className={`h-5 w-5 ${isDragOver ? 'text-primary-500' : 'text-gray-400'}`} />
                    <span className="text-sm dark:text-white">
                      {isDragOver ? 'Drop videos here' : 'Click to upload or drag & drop'}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">MP4, WebM, MOV</span>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                onChange={handleFileChange}
                className="hidden"
                multiple
              />

              {/* Dropbox folder URL input — admin only, never in artist view */}
              {isAdmin && !isArtistView && (
              <div className="mt-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={folderUrl}
                    onChange={(e) => setFolderUrl(e.target.value)}
                    placeholder="or paste a Dropbox folder URL"
                    disabled={isProcessingFolder || isUploading || loading}
                    className="flex-1 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                  />
                  {isDropboxFolderUrl(folderUrl) && (
                    <button
                      type="button"
                      onClick={processDropboxFolder}
                      disabled={isProcessingFolder || !folderUrl.trim()}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 flex-shrink-0"
                    >
                      {isProcessingFolder ? (
                        <>
                          <LoaderIcon className="h-3.5 w-3.5 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <FolderDown className="h-3.5 w-3.5" />
                          Import folder
                        </>
                      )}
                    </button>
                  )}
                </div>
                {isDropboxFolderUrl(folderUrl) && (
                  <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                    Dropbox folder detected — click "Import folder" to download all videos.
                  </p>
                )}
              </div>
              )}

              {/* Folder processing logs */}
              {folderProcessLogs.length > 0 && (
                <div className="mt-2 p-3 bg-gray-900 rounded-md max-h-40 overflow-y-auto font-mono text-xs">
                  {folderProcessLogs.map((log, index) => (
                    <div
                      key={index}
                      className={`py-0.5 ${
                        log.type === 'error' ? 'text-red-400' :
                        log.type === 'success' ? 'text-green-400' :
                        log.type === 'progress' ? 'text-blue-400' :
                        'text-gray-300'
                      }`}
                    >
                      {log.type === 'progress' && <span className="animate-pulse">⏳ </span>}
                      {log.type === 'success' && <span>✅ </span>}
                      {log.type === 'error' && <span>❌ </span>}
                      {log.type === 'info' && <span>ℹ️ </span>}
                      {log.message}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Upload Progress Logs */}
              {uploadLogs.length > 0 && (
                <div className="mt-2 p-3 bg-gray-900 rounded-md max-h-32 overflow-y-auto font-mono text-xs">
                  {uploadLogs.map((log, index) => (
                    <div 
                      key={index} 
                      className={`py-0.5 ${
                        log.type === 'error' ? 'text-red-400' : 
                        log.type === 'success' ? 'text-green-400' : 
                        log.type === 'progress' ? 'text-blue-400' : 
                        'text-gray-300'
                      }`}
                    >
                      {log.type === 'progress' && <span className="animate-pulse">⏳ </span>}
                      {log.type === 'success' && <span>✅ </span>}
                      {log.type === 'error' && <span>❌ </span>}
                      {log.type === 'info' && <span>ℹ️ </span>}
                      {log.message}
                    </div>
                  ))}
                </div>
              )}
              
              {uploadedUrls.length > 0 && (
                <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-md">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Uploaded videos:</p>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {uploadedUrls.map((url, index) => {
                      const hasThumbnail = thumbnails[url] !== undefined;
                      const fileName = uploadedFileNames[url] || '';
                      return (
                        <div 
                          key={index} 
                          className="text-xs text-gray-600 dark:text-gray-400 py-1 group relative border-b border-gray-200 dark:border-gray-600 last:border-b-0 pb-2"
                          onMouseEnter={() => setHoveredUrl(url)}
                          onMouseLeave={() => setHoveredUrl(null)}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <input
                              type="text"
                              value={fileName}
                              onChange={(e) => {
                                setUploadedFileNames(prev => ({ ...prev, [url]: e.target.value }));
                              }}
                              placeholder="Enter video name"
                              className="flex-1 px-2 py-1 text-sm font-medium rounded border border-gray-300 dark:border-gray-500 dark:bg-gray-600 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setUploadedUrls(prev => prev.filter((_, i) => i !== index));
                                // Also remove thumbnail and filename if exists
                                if (thumbnails[url]) {
                                  setThumbnails(prev => {
                                    const newThumbnails = {...prev};
                                    delete newThumbnails[url];
                                    return newThumbnails;
                                  });
                                }
                                setUploadedFileNames(prev => {
                                  const newFileNames = {...prev};
                                  delete newFileNames[url];
                                  return newFileNames;
                                });
                              }}
                              className="ml-2 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400"
                              title="Remove file"
                            >
                              <XIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="flex items-center text-gray-400 dark:text-gray-500">
                            <span className="mr-1">↳</span>
                            {hasThumbnail && (
                              <Image className="h-3 w-3 mr-1 text-green-500" />
                            )}
                            <span className="truncate">{url}</span>
                          </div>
                          
                          {/* Thumbnail preview on hover */}
                          {hoveredUrl === url && thumbnails[url] && (
                            <div className="absolute right-0 bottom-full mb-2 z-10">
                              <div className="bg-gray-900 rounded-md p-1 shadow-lg">
                                <img 
                                  src={thumbnails[url]} 
                                  alt="Thumbnail" 
                                  className="w-24 h-auto rounded"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
          <motion.div 
            className="flex justify-end space-x-2 pt-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <motion.button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
              disabled={loading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Cancel
            </motion.button>
            <motion.button
              type="submit"
              disabled={loading}
              className="btn disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={!loading ? { scale: 1.05 } : {}}
              whileTap={!loading ? { scale: 0.95 } : {}}
            >
              {loading ? (
                <>
                  <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Submitting...
                </>
              ) : (
                'Submit URLs'
              )}
            </motion.button>
          </motion.div>
        </form>
      </motion.div>
    </div>
  );
}